import React, { useMemo, useState } from 'react';

import { openBlob } from '@apis/common';
import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { dashboardDeliveryRequestLogApis } from '@apis/next/deliveryRequestLogApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import { DeliveryRequestBatchSubmitDialog } from '@components/admin/booking/DeliveryRequestBatchSubmitDialog';
import { DeliveryRequestFormDialog } from '@components/admin/booking/DeliveryRequestFormDialog';
import { DeliveryRequestURLSubmitDialog } from '@components/admin/booking/DeliveryRequestURLSubmitDialog';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { useSearch } from '@hooks/next/useSearch';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useDeliveryInstruction } from '@hooks/queries/dashboard/useDeliveryInstruction';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { DeliveryRequestHelper } from '@models/next/DeliveryRequestHelper';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { mapOptional, toJST } from '@utils/types';
import { Document, Page } from 'react-pdf';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: `${deliveryRequestStrings.deliveryRequestFile}`,
    deliveryInfoTitle: `${deliveryRequestStrings.deliveryInfo}補足`,
    copyLink: 'リンクをコピー',
    exportPdf: 'PDF出力',
    deliveryURLsCreatedPrompt: '配車資料のURLが作成しました：',
    lastUpdateUserAndTime: '最新更新者と更新時間',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    ...common.flexCenter,
    margin: 16,
    marginBottom: 200,
  },
  operationPanel: {
    ...common.flexCenter,
    ...common.middleText,
    position: 'fixed',
    bottom: 0,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',

    width: '100%',
    boxShadow: '0px -4px 4px rgba(0, 0, 0, 0.25);',
  },
  operationBox: {
    ...common.flexInline,
    ...common.middleText,
    background: theme.palette.primary.contrastText,
    padding: '10px 0px',
    maxWidth: 960,
    width: '100%',
  },
  info: {
    flex: 1,
    ...common.flexColumn,
  },
  operation: {
    ...common.flexCenter,
    columnGap: 15,
  },
}));

export const DeliveryRequestDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(
    z.object({
      id: z.number(),
      d2d_no: z.string(),
    }),
  );

  const { confirm, renderDialog } = useConfirmDialog();

  const [openEdit, setOpenEdit] = useState(false);
  const [openSubmit, setOpenSubmit] = useState(false);
  const [openURLSubmit, setOpenURLSubmit] = useState(false);

  const userQuery = useUser();

  const preAlertQuery = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: search.d2d_no,
    },
  });

  const bookingInquiryQuery = useBookingInquiryDetailByD2dNo(search.d2d_no);

  const deliveryRequestQuery = dashboardDeliveryRequestApis.useGet(
    '/dashboard/delivery-request/:id',
    {
      params: { id: search.id },
    },
  );

  const deliveryInstructionQuery = useDeliveryInstruction(search.d2d_no);
  const pdfDeliveryRequestQuery = dashboardPdfApis.useGet(
    '/dashboard/pdf/DeliveryRequest/:id',
    {
      params: { id: search.id },
    },
    {
      staleTime: Infinity,
    },
  );

  const deliveryRequestLogQuery = dashboardDeliveryRequestLogApis.useGet(
    '/dashboard/delivery-request-log/:id',
    {
      params: { id: search.id },
    },
  );

  const pdfUrl = useMemo(
    () =>
      pdfDeliveryRequestQuery.data ? URL.createObjectURL(pdfDeliveryRequestQuery.data) : undefined,
    [pdfDeliveryRequestQuery.data],
  );

  const renderOperationPanel = () => {
    const canEdit =
      deliveryRequestQuery.data && DeliveryRequestHelper.canEdit(deliveryRequestQuery.data);

    const canMutate =
      deliveryRequestQuery.data && DeliveryRequestHelper.canMutate(deliveryRequestQuery.data);

    const canSubmit =
      deliveryRequestQuery.data && DeliveryRequestHelper.canSubmit(deliveryRequestQuery.data);

    return (
      <div className={classes.operationPanel}>
        <div className={classes.operationBox}>
          <div className={classes.info}>
            <div>
              {deliveryRequestStrings.createdAt}
              {': '}
              {deliveryRequestQuery.data?.created_at
                ? toJST(new Date(deliveryRequestQuery.data.created_at))
                : '-'}
            </div>

            <div>
              {deliveryRequestStrings.creator}:{' '}
              {mapOptional(deliveryRequestQuery.data?.creator, (u) => new UserHelper(u).fullname)}
            </div>

            <div>
              {deliveryRequestStrings.submittedAt}:{' '}
              {mapOptional(deliveryRequestQuery.data, (d) =>
                mapOptional(DeliveryRequestHelper.getFirstMailSubmittedAt(d), (d) =>
                  toJST(new Date(d)),
                ),
              )}
            </div>

            <div>
              {deliveryRequestStrings.submissionReceiver}:{' '}
              {deliveryRequestQuery?.data?.partner?.name_ja ?? '-'}
            </div>

            <div>
              {strings.lastUpdateUserAndTime}:{' '}
              {mapOptional(deliveryRequestQuery.data, (deliveryRequest) =>
                mapOptional(deliveryRequestLogQuery.data, (logs) =>
                  mapOptional(
                    DeliveryRequestHelper.getLastUpdateUserAndTime(deliveryRequest, logs),
                    (d) =>
                      `${d.action} ${d.fullname ?? '-'} ${d.time ? toJST(new Date(d.time)) : '-'}`,
                  ),
                ),
              ) ?? '-'}
            </div>

            <a
              href={url('/admin/booking/PrintDeliveryRequestLog') + `?id=${search.id}`}
              target="_blank"
              className={classes.link}
            >
              {deliveryRequestStrings.changeLog}
            </a>
          </div>

          <div className={classes.operation}>
            <Button
              color="primary"
              text={strings.exportPdf}
              type="button"
              shape="round"
              size="small"
              disabled={!pdfDeliveryRequestQuery.isSuccess}
              onClick={async () => {
                openBlob(
                  pdfDeliveryRequestQuery.data!,
                  `${deliveryRequestStrings.deliveryRequestDocuments}.pdf`,
                  'application/pdf',
                );
              }}
            />

            <Button
              color={
                canEdit
                  ? foldMessagesEither(
                      canEdit,
                      () => 'grey',
                      () => 'primary',
                    )
                  : 'grey'
              }
              text={formStrings.edit}
              type="button"
              shape="round"
              size="small"
              onClick={() =>
                canEdit &&
                foldMessagesEither(
                  canEdit,
                  ({ messages }) => {
                    confirm({ messages });
                  },
                  () => {
                    setOpenEdit(true);
                  },
                )
              }
            />

            <Button
              color={
                canSubmit
                  ? foldMessagesEither(
                      canSubmit,
                      () => 'grey',
                      () => 'accent',
                    )
                  : 'grey'
              }
              text={deliveryRequestStrings.mailSubmission}
              type="button"
              shape="round"
              size="small"
              onClick={() =>
                canMutate &&
                foldMessagesEither(
                  canMutate,
                  ({ messages }) => {
                    confirm({ messages });
                  },
                  () => {
                    setOpenSubmit(true);
                  },
                )
              }
            />

            <Button
              color={
                canSubmit
                  ? foldMessagesEither(
                      canSubmit,
                      () => 'grey',
                      () => 'accent',
                    )
                  : 'grey'
              }
              text={strings.copyLink}
              type="button"
              shape="round"
              size="small"
              onClick={() =>
                canSubmit &&
                foldMessagesEither(
                  canSubmit,
                  ({ messages }) => {
                    confirm({ messages });
                  },
                  () => {
                    setOpenURLSubmit(true);
                  },
                )
              }
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      title={
        deliveryRequestQuery.data
          ? `${strings.title} - ${deliveryRequestQuery.data.delivery_request_no}`
          : strings.title
      }
      loading={
        deliveryRequestQuery.isLoading ||
        pdfDeliveryRequestQuery.isLoading ||
        deliveryRequestQuery.isLoading ||
        userQuery.isLoading ||
        preAlertQuery.isLoading ||
        bookingInquiryQuery.isLoading
      }
      errors={[
        deliveryRequestQuery.error,
        pdfDeliveryRequestQuery.error,
        deliveryRequestQuery.error,
        userQuery.error,
        preAlertQuery.error,
        bookingInquiryQuery.error,
      ]}
      classes={{
        background: classes.background,
      }}
    >
      <div className={classes.container}>
        <Document key={pdfUrl} file={pdfUrl} loading="">
          <Page pageNumber={1} width={900} />
          <Page pageNumber={2} width={900} />
        </Document>
      </div>

      {renderOperationPanel()}
      <DeliveryRequestFormDialog
        type="update"
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        d2dNo={search.d2d_no}
        deliveryInstruction={deliveryInstructionQuery.data!}
        deliveryRequest={deliveryRequestQuery.data}
      />
      <DeliveryRequestBatchSubmitDialog
        open={openSubmit}
        onClose={() => setOpenSubmit(false)}
        action="BATCH_MAIL_SUBMIT"
        deliveryRequests={[deliveryRequestQuery.data!]}
      />

      {deliveryRequestQuery.data && (
        <DeliveryRequestURLSubmitDialog
          deliveryRequest={deliveryRequestQuery.data}
          open={openURLSubmit}
          onClose={() => setOpenURLSubmit(false)}
        />
      )}

      {renderDialog()}
    </Layout>
  );
};
